import {
  Level,
  LogDocument,
  LogInput,
} from '$extensionSrc/__generated__/unauth/graphql'
import { getUnauthClient } from '$extensionSrc/apollo'
import BuildConfig from '$extensionSrc/buildConfig'
import { getSpeed } from '$extensionSrc/data/speed'
import { getOrSetDoNotTrack } from '$extensionSrc/metrics/dnt'
import {
  getAnonymousUserId,
  getExtensionInstallationId,
} from '$extensionSrc/metrics/idGetSet'

import * as canIUse from './canIUse'
import * as communication from './communication'
import { getUserAccountId } from './getUserAccountData'
import { _debug } from './logging'
import * as pii from './pii'

export async function mutate(logInput: LogInput): Promise<void> {
  await getUnauthClient().mutate({
    mutation: LogDocument,
    variables: { logInput },
  })
}

type Properties = {
  element?: HTMLElement
  network?: number
  pageLoad?: number
}

type Data = {
  anonymousUserId: string
  extensionInstallationId?: string
  level: Level
  properties?: Properties
  userAccountId?: string
}

export const remoteLog = {
  async _send(
    incomingMessage: string,
    incomingProperties: Properties | undefined,
    level: Level,
  ): Promise<void> {
    // Remote logs are stored for a time on Mezmo. So, if users have set DNT,
    // we don't want to send anything.
    const doNotTrack = await getOrSetDoNotTrack()
    if (doNotTrack) {
      return
    }

    const message = pii.redact(incomingMessage)

    const anonymousUserId = await getAnonymousUserId()
    if (!anonymousUserId) {
      return
    }
    const extensionInstallationId = await getExtensionInstallationId()
    const userAccountId = await getUserAccountId()

    let properties = {
      ...incomingProperties,
    }
    if (canIUse.chromeStorage()) {
      const speed = await getSpeed()
      if (speed?.network) {
        properties = {
          ...properties,
          network: speed.network,
        }
      }
      if (speed?.pageLoad) {
        properties = {
          ...properties,
          pageLoad: speed.pageLoad,
        }
      }
    }

    if (BuildConfig.REACT_APP_ENV === 'development') {
      // Add properties to the data object in a way that is visually appealing
      // in the browser console
      let data: Data = { level, anonymousUserId }
      if (userAccountId) {
        data = { ...data, userAccountId }
      }
      if (extensionInstallationId) {
        data = { ...data, extensionInstallationId }
      }
      if (properties) {
        data = { ...data, properties }
      }

      _debug(data, `remoteLog: ${message}`, level)
    }

    const logInput: LogInput = {
      anonymousUserId,
      extensionInstallationId: extensionInstallationId || null,
      level,
      message,
      properties: properties ? JSON.stringify(properties) : null,
      userAccountId: userAccountId || null,
    }

    if (typeof chrome !== 'undefined' && chrome.runtime) {
      await communication.sendRemoteLog(logInput)
    } else {
      await mutate(logInput)
    }
  },

  async debug(message: string, properties?: Properties): Promise<void> {
    this._send(message, properties, Level.Debug)
  },

  async error(message: string, properties?: Properties): Promise<void> {
    this._send(message, properties, Level.Error)
  },

  async info(message: string, properties?: Properties): Promise<void> {
    this._send(message, properties, Level.Info)
  },

  async warn(message: string, properties?: Properties): Promise<void> {
    this._send(message, properties, Level.Warn)
  },
}
